import { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Spin,
  Select,
  InputNumber,
  Alert,
  App,
  Row,
  Col,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { IPP_VENDORS } from '@graphql/vendorList.gql';
import { VENDOR_CO_PARTY_INFO } from '@graphql/counterParty.gql';
import { useVendorList } from '@pages/vendor-list/useVendorList';
import PageLayout from '@components/layout/PageLayout';
import styles from './VendorForm.module.css';
import dayjs from 'dayjs';
import { NSPButton } from '@components/ui/NSPButton';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import NSPCheckbox from '@components/ui/NSPCheckbox';
import AddCoPartyBlade from '@components/blades/add-co-party-blade/AddCoPartyBlade';
import ViewCoPartyBlade from '@components/blades/view-co-party-blade/ViewCoPartyBlade';

const { Option } = Select;

interface VendorFormProps {
  mode?: 'add' | 'edit';
}

function VendorFormInner({ mode = 'add' }: Readonly<VendorFormProps>) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const vendorHook = useVendorList();
  const { message } = App.useApp();

  const [vendor, setVendor] = useState<any>(null);
  const [savedVendor, setSavedVendor] = useState<any>(null);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);
  const [saveErrorMsg, setSaveErrorMsg] = useState<string | null>(null);

  const [isAddCoPartyBladeOpen, setIsAddCoPartyBladeOpen] = useState(false);
  const [isViewCoPartyBladeOpen, setIsViewCoPartyBladeOpen] = useState(false);

  const isEditMode = mode === 'edit' && !!id;

  const { loading: fetchingVendor } = useQuery(IPP_VENDORS, {
    variables: { page: 1, limit: 1000 },
    skip: !isEditMode,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (isEditMode && id) {
        const foundVendor = data?.ippVendorsPaginated?.nodes?.find(
          (v: any) => v.id === Number.parseInt(id, 10)
        );
        if (foundVendor) {
          setVendor(foundVendor);
          setSavedVendor(foundVendor);
        } else {
          message.error('Vendor not found');
          navigate('/vendor-list');
        }
      }
    },
    onError: () => {
      message.error('Failed to load vendor details');
      navigate('/vendor-list');
    },
  });

  useEffect(() => {
    if (vendor) {
      form.setFieldsValue({
        vendorName: vendor.vendorName ?? '',
        vendorContactNumber: vendor.vendorContactNumber ?? '',
        vendorAddress: vendor.vendorAddress ?? '',
        vendorEmailId: vendor.vendorEmailId ?? '',
        vendorEmailId1: vendor.vendorEmailId1 ?? '',
        vendorEmailId2: vendor.vendorEmailId2 ?? '',
        vendorEmailId3: vendor.vendorEmailId3 ?? '',
        purchaseOrderNo: vendor.purchaseOrderNo ?? '',
        contactName: vendor.contactName ?? '',
        departmentName: vendor.departmentName ?? '',
        poCalendarYear: vendor.poCalendarYear ?? '',
        vendorInvoicePrefix: vendor.vendorInvoicePrefix ?? '',
        vendorDueDate: vendor.vendorDueDate ?? '',
        commercialStatus: vendor.commercialStatus ?? 'Active',
        statusDate: vendor.statusDate ? dayjs(vendor.statusDate) : dayjs(),
        commercialOperationDate: vendor.commercialOperationDate
          ? dayjs(vendor.commercialOperationDate)
          : null,
        needDetailsWithInvoice:
          vendor.needDetailsWithInvoice === 'Yes' || vendor.needDetailsWithInvoice === true,
      });
    } else if (!isEditMode) {
      form.resetFields();
      form.setFieldsValue({ statusDate: dayjs() });
    }
  }, [vendor, form, isEditMode]);

  const handleSubmit = async () => {
    setSaveSuccessMsg(null);
    setSaveErrorMsg(null);

    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const formatDate = (val: any) => {
      if (!val) return null;
      const d = dayjs(val);
      return d.isValid() ? d.toISOString() : null;
    };

    const payload = {
      vendorName: values.vendorName,
      vendorContactNumber: values.vendorContactNumber,
      vendorAddress: values.vendorAddress,
      vendorEmailId: values.vendorEmailId,
      vendorEmailId1: values.vendorEmailId1 || null,
      vendorEmailId2: values.vendorEmailId2 || null,
      vendorEmailId3: values.vendorEmailId3 || null,
      purchaseOrderNo: values.purchaseOrderNo,
      contactName: values.contactName || null,
      departmentName: values.departmentName || null,
      poCalendarYear: values.poCalendarYear || null,
      vendorInvoicePrefix: values.vendorInvoicePrefix || null,
      vendorDueDate: values.vendorDueDate || null,
      commercialStatus: values.commercialStatus,
      statusDate: formatDate(values.statusDate),
      commercialOperationDate: formatDate(values.commercialOperationDate),
      needDetailsWithInvoice: values.needDetailsWithInvoice ? 'Yes' : 'No',
    };

    let result: any;
    if (isEditMode && vendor) {
      result = await vendorHook.handleUpdateVendor(vendor.id, payload);
    } else {
      result = await vendorHook.handleCreateVendor(payload);
    }

    if (result?.success) {
      setSavedVendor(result.data);
      if (!isEditMode) {
        setVendor(result.data);
      }

      setSaveSuccessMsg(isEditMode ? 'Vendor updated successfully' : 'Vendor created successfully');
    } else if (result != null) {
      setSaveErrorMsg(result.error || 'Failed to save vendor');
    }
  };

  const handleAddNewMeter = () => {
    const vendorData = savedVendor;
    if (!vendorData) return;


    const rawNeed = vendorData.needDetailsWithInvoice;
    const needDetailsNormalised =
      rawNeed === true || rawNeed === 'Yes' ? 'Yes'
        : rawNeed === false || rawNeed === 'No' ? 'No'
          : undefined;

    navigate('/core-info/new', {
      state: {
        prefill: {
          ipp: {
            ippId: vendorData.id,
            vendorName: vendorData.vendorName ?? '',
            //  Pass the raw string value; empty string if null/undefined so
            //    the disabled Input renders the value correctly instead of blank.
            purchaseOrderNo: vendorData.purchaseOrderNo ?? '',
            needDetailsWithInvoice: needDetailsNormalised,
          },
        },
      },
    });
  };

  const handleCancel = () => navigate('/vendor-list');

  const canAddMeter = !!savedVendor;
  const isSaving = vendorHook.creating || vendorHook.updatingVendor || fetchingVendor;

  const { data: coPartyInfoData, refetch: refetchCoPartyInfo } = useQuery(VENDOR_CO_PARTY_INFO, {
    variables: { vendorId: savedVendor?.id },
    skip: !savedVendor,
    fetchPolicy: 'network-only',
  });
  const coPartyInfo = coPartyInfoData?.vendorCoPartyInfo;
  const canAddCoParty = canAddMeter && !!coPartyInfo?.eligible && !coPartyInfo?.hasCoParty;

  return (
    <PageLayout title={isEditMode ? 'Edit Vendor' : 'Add New Vendor'}>
      {fetchingVendor ? (
        <div className={styles.spinloader}>
          <Spin size="large" tip="Loading vendor details..." data-testid="loading-spinner">
            <div style={{ minHeight: 200 }} />
          </Spin>
        </div>
      ) : (
        <div className={styles.vendorFormContainer}>

          {saveSuccessMsg && (
            <Alert
              message="Success"
              description={saveSuccessMsg}
              type="success"
              showIcon
              closable
              onClose={() => setSaveSuccessMsg(null)}
              style={{ marginBottom: 16 }}
              data-testid="vendor-success-alert"
            />
          )}

          {saveErrorMsg && (
            <Alert
              message="Error"
              description={saveErrorMsg}
              type="error"
              showIcon
              closable
              onClose={() => setSaveErrorMsg(null)}
              style={{ marginBottom: 16 }}
              data-testid="vendor-error-alert"
            />
          )}



          {!isEditMode && canAddMeter && (
            <Alert
              message="Vendor saved!"
              description={`"${savedVendor?.vendorName}" has been created. You can now add a new meter for this vendor.`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              data-testid="vendor-saved-alert"
            />
          )}

          <Form
            form={form}
            layout="vertical"
            className={styles.vendorForm}
            data-testid="vendor-form"
            data-mode={isEditMode ? 'edit' : 'add'}
            data-vendor-id={savedVendor?.id != null ? String(savedVendor.id) : undefined}
          >

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="vendorName" data-testid="vendor-name-field" label="Vendor Name" rules={[{ required: true, message: 'Please enter Vendor Name' }]}>
                  <Input placeholder="Enter Vendor Name (e.g., GreenTech Ltd.)" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="vendorContactNumber"
                  data-testid="contact-number-field"
                  label="Contact Number (Optional)"
                  rules={[
                    { pattern: /^\d{10}$/, message: 'Contact Number must be exactly 10 digits' },
                  ]}
                >
                  <Input placeholder="Enter 10-digit Contact Number" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="contactName" data-testid="contact-name-field" label="Contact Name (Optional)">
                  <Input placeholder="Enter Contact Person Name" maxLength={40} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="departmentName" data-testid="department-name-field" label="Department Name (Optional)">
                  <Input placeholder="Enter Department Name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={24}>
                <Form.Item name="vendorAddress" data-testid="address-field" label="Address" rules={[{ required: true, message: 'Please enter Address' }]}>
                  <Input.TextArea rows={3} placeholder="Enter Address" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="vendorEmailId"
                  data-testid="primary-email-field"
                  label="Primary Invoice Email"
                  rules={[
                    { required: true, message: 'Please enter Email' },
                    { type: 'email', message: 'Please enter a valid email address' },
                  ]}
                >
                  <Input placeholder="Enter Primary Email" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="vendorEmailId1" data-testid="additional-email-1-field" label="Additional Invoice Email 1 (Optional)" rules={[{ type: 'email', message: 'Please enter a valid email address' }]}>
                  <Input placeholder="Enter Additional Email 1 (Optional)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="vendorEmailId2" data-testid="additional-email-2-field" label="Additional Invoice Email 2 (Optional)" rules={[{ type: 'email', message: 'Please enter a valid email address' }]}>
                  <Input placeholder="Enter Additional Email 2 (Optional)" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="vendorEmailId3" data-testid="additional-email-3-field" label="Additional Invoice Email 3 (Optional)" rules={[{ type: 'email', message: 'Please enter a valid email address' }]}>
                  <Input placeholder="Enter Additional Email 3 (Optional)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="purchaseOrderNo" data-testid="purchase-order-no-field" label="Purchase Order No (Optional)">
                  <Input placeholder="Enter Purchase Order No (e.g., NSP1001)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="poCalendarYear"
                  data-testid="po-calendar-year-field"
                  label="PO Calendar Year"
                  dependencies={['purchaseOrderNo']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const po = getFieldValue('purchaseOrderNo');
                        if (po && !value) {
                          return Promise.reject(new Error('PO Calendar Year is required when Purchase Order No is set'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber min={2000} max={2100} placeholder="Enter Calendar Year (e.g., 2025)" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="vendorInvoicePrefix" data-testid="invoice-prefix-field" label="Vendor Invoice Prefix" rules={[{ required: true, message: 'Vendor Invoice Prefix is required' }]}>
                  <Input placeholder="Enter Prefix (e.g., INV-GT)" maxLength={20} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="commercialStatus" data-testid="commercial-status-field" label="Commercial Status" initialValue="Active" rules={[{ required: true, message: 'Please select Commercial Status' }]}>
                  <Select placeholder="Select Status">
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="vendorDueDate"
                  data-testid="due-date-field"
                  label="Vendor Monthly Due Date"
                  rules={[
                    { required: true, message: 'Please enter due day (1–31)' },
                    { type: 'number', min: 1, max: 31, message: 'Must be between 1 and 31' },
                  ]}
                >
                  <InputNumber min={1} max={31} placeholder="e.g., 15 or 25" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="statusDate" data-testid="status-date-field" label="Date of Status" initialValue={dayjs()}>
                  <NSPDatePicker format="YYYY-MM-DD" style={{ width: '100%' }} allowClear />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="commercialOperationDate" data-testid="commercial-operation-date-field" label="Commercial Operation Date" tooltip="Usually the first day of the month following commercial operation">
                  <NSPDatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="needDetailsWithInvoice" data-testid="need-details-field" valuePropName="checked" initialValue={false}>
              <NSPCheckbox data-testid="need-details-checkbox">Yes, need details with invoice</NSPCheckbox>
            </Form.Item>

            {coPartyInfo?.hasCoParty && (
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item label="Percentage Payable to Primary Vendor" data-testid="percentage-payable-field">
                    <Input
                      disabled
                      className={styles.percentagePayableInput}
                      value={
                        coPartyInfo.percentagePayableToPrimaryVendor != null
                          ? `${coPartyInfo.percentagePayableToPrimaryVendor}%`
                          : '—'
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <div className={styles.footerActions}>
              <div className={styles.leftActions}>
                <NSPButton
                  onClick={handleAddNewMeter}
                  data-testid="add-meter-button"
                  disabled={!canAddMeter}
                  title={
                    !canAddMeter
                      ? 'Save the vendor first to enable this button'
                      : `Add a new meter for "${savedVendor?.vendorName}"`
                  }
                >
                  Add New Meter
                </NSPButton>
                {coPartyInfo?.hasCoParty ? (
                  <NSPButton
                    onClick={() => setIsViewCoPartyBladeOpen(true)}
                    data-testid="view-co-party-button"
                    title={`View Co-party "${coPartyInfo.coPartyVendorName}"`}
                  >
                    View Co-party
                  </NSPButton>
                ) : (
                  <NSPButton
                    onClick={() => setIsAddCoPartyBladeOpen(true)}
                    data-testid="add-co-party-button"
                    disabled={!canAddCoParty}
                    title={
                      !canAddMeter
                        ? 'Save the vendor first to enable this button'
                        : !canAddCoParty
                          ? 'This vendor does not have exactly one meter'
                          : `Add a new Co-party for "${savedVendor?.vendorName}"`
                    }
                  >
                    Add a new Co-party
                  </NSPButton>
                )}
              </div>

              <div className={styles.rightActions}>
                <NSPButton onClick={handleCancel} data-testid="cancel-button">Cancel</NSPButton>
                <NSPButton onClick={handleSubmit} loading={isSaving} data-testid="save-button">Save</NSPButton>
              </div>
            </div>
          </Form>

          <AddCoPartyBlade
            open={isAddCoPartyBladeOpen}
            onClose={() => setIsAddCoPartyBladeOpen(false)}
            primaryVendor={savedVendor}
            onSaved={() => refetchCoPartyInfo()}
          />

          <ViewCoPartyBlade
            open={isViewCoPartyBladeOpen}
            onClose={() => setIsViewCoPartyBladeOpen(false)}
            coPartyVendorId={coPartyInfo?.coPartyVendorId ?? null}
            onSaved={() => refetchCoPartyInfo()}
          />
        </div>
      )}
    </PageLayout>
  );
}

export default function VendorForm(props: Readonly<VendorFormProps>) {
  return (
    <App>
      <VendorFormInner {...props} />
    </App>
  );
}